import { locationLabels } from '../../../util/locationLabels';
import { useSupportedCountries } from '../../../util/platformAccess';
import React, { memo, useMemo } from "react";
import {
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Checkbox,
    FormControlLabel,
    Link,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import BusinessSettings from "../../../components/business/business/businessSettings";
import { SMS_CONSENT_TEXT } from "../../../components/legal/SmsConsentCheckbox";
import { GET_ALL_COUNTRIES } from "../../../graphql/queries/countryStateCity";
import TimezoneSelector from "../../../util/timezoneSelector";

const UserPageGeneral = memo(({
    defaultBusinessId,
    defaultBusinessLoading,
    defaultBusinessError,
    businesses,
    handleSelectDefaultBusinessChange,
    username,
    setUsername,
    setUpdated,         // This is setGeneralUpdated passed from parent
    email,
    phone,
    setPhone,
    address,
    setAddress,
    city,
    setCity,
    state,
    setState,
    country,
    setCountry,
    zipcode,
    setZipcode,
    smsOptIn,
    setSmsOptIn,
    setSmsConsentChanged,
    selectedTimeZoneId,
    token,
    handleTimezoneSelect,
    handleSubmit,
    updated, 
    notificationRef, 
    desc,
    setDesc,
    errors
}) => {
    const { data: countryDataRaw, loading: loadingCountries } = useQuery(GET_ALL_COUNTRIES);
    const { countries: supportedCountries } = useSupportedCountries();
    const countries = useMemo(() => (countryDataRaw?.getAllCountries || []).filter(c => supportedCountries.some(s => s.code === c.code.toUpperCase())), [countryDataRaw, supportedCountries]);
    const selectedCountry = useMemo(
        () => countries.find((item) => String(item?.name || "") === String(country || "")) || null,
        [countries, country]
    );
    const labels = locationLabels(selectedCountry?.code);
    const availableStates = useMemo(() => selectedCountry?.states || [], [selectedCountry]);
    const selectedStateRecord = useMemo(
        () => availableStates.find((item) => String(item?.name || "") === String(state || "")) || null,
        [availableStates, state]
    );
    const availableCities = useMemo(() => selectedStateRecord?.cities || [], [selectedStateRecord]);
    const hasSelectedCountry = countries.some((item) => String(item?.name || "") === String(country || ""));
    const hasSelectedState = availableStates.some((item) => String(item?.name || "") === String(state || ""));
    const hasSelectedCity = availableCities.some((item) => String(item?.name || "") === String(city || ""));

    return (
        <Typography variant="body1" align="justify" sx={{ p: 2 }}>
            <>
                <BusinessSettings source="user-profile" />
                <FormControl margin="normal" fullWidth>
                    <InputLabel id="time-format-label">Default Business Profile</InputLabel>
                    <Select
                        label="Default Business Profile"
                        variant="outlined"
                        value={defaultBusinessId || ""}
                        onChange={(e) => {
                            handleSelectDefaultBusinessChange(e);
                        }}
                        disabled={defaultBusinessLoading || !businesses?.length}
                    >
                        {!businesses?.length && (
                            <MenuItem value="" disabled>
                                No businesses available
                            </MenuItem>
                        )}
                        {businesses?.map((bus) => (
                            <MenuItem key={bus?.businessBasic?.id} value={bus?.businessBasic?.id}>
                                {bus?.businessBasic?.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {defaultBusinessLoading && <CircularProgress size={24} />}
                    {defaultBusinessError && (
                        <Typography color="error" variant="caption">
                            {defaultBusinessError}
                        </Typography>
                    )}
                </FormControl>
            </>
          <FormControl margin="normal" fullWidth>
              <TextField
                    label="Username"
                  variant="outlined"
                  value={username}
                  onChange={(e) => {
                      setUsername(e.target.value);
                      setUpdated(true); // Mark as updated when username changes
                  }}
                  inputProps={{ maxLength: 50 }}
                  helperText={`${username.length} / 50`} 
              />
          </FormControl>
          <FormControl margin="normal" fullWidth>
                <TextField label="Email" variant="outlined" value={email} disabled />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    label="Phone"
                    variant="outlined"
                    value={phone || ""}
                    onChange={(e) => {
                        const raw = e.target.value || "";
                        const normalized = raw.replace(/[^+\d]/g, "").slice(0, 16);
                        setPhone(normalized);
                        setUpdated(true);
                    }}
                    placeholder="+14255551234 or +251911234567"
                    InputProps={{

                    }}
                    helperText="Include the country code: +1 for USA or +251 for Ethiopia."
                />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                    Location
                </Typography>
                <TextField
                    label="Country"
                    variant="outlined"
                    value={country || ""}
                    select
                    disabled={loadingCountries}
                    onChange={(e) => {
                        setCountry(e.target.value);
                        setState("");
                        setCity("");
                        setUpdated(true);
                    }}
                    helperText={loadingCountries ? "Loading countries..." : " "}
                >
                    {!!country && !hasSelectedCountry ? (
                        <MenuItem value={country}>{country}</MenuItem>
                    ) : null}
                    {countries.map((item) => (
                        <MenuItem key={item.id} value={item.name}>
                            {item.name}
                        </MenuItem>
                    ))}
                </TextField>
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    label={labels.region}
                    variant="outlined"
                    value={state || ""}
                    select
                    disabled={!country || loadingCountries}
                    onChange={(e) => {
                        setState(e.target.value);
                        setCity("");
                        setZipcode("");
                        setUpdated(true);
                    }}
                    helperText={
                        !country
                            ? "Select country first"
                            : availableStates.length
                                ? " "
                                : `No ${labels.region.toLowerCase()} choices found for this country`
                    }
                >
                    {!!state && !hasSelectedState ? (
                        <MenuItem value={state}>{state}</MenuItem>
                    ) : null}
                    {availableStates.map((item) => (
                        <MenuItem key={item.id} value={item.name}>
                            {item.name}
                        </MenuItem>
                    ))}
                </TextField>
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    label={labels.city}
                    variant="outlined"
                    value={city || ""}
                    select
                    disabled={!country || !state || loadingCountries}
                    onChange={(e) => {
                        setCity(e.target.value);
                        setZipcode("");
                        setUpdated(true);
                    }}
                    helperText={
                        !country
                            ? "Select country first"
                            : !state
                                ? `Select ${labels.region.toLowerCase()} first`
                                : availableCities.length
                                    ? " "
                                    : "No cities found for this location"
                    }
                >
                    {!!city && !hasSelectedCity ? (
                        <MenuItem value={city}>{city}</MenuItem>
                    ) : null}
                    {availableCities.map((item) => (
                        <MenuItem key={item.id} value={item.name}>
                            {item.name}
                        </MenuItem>
                    ))}
                </TextField>
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    label={labels.postal}
                    variant="outlined"
                    value={zipcode || ""}
                    disabled={!country || !state || !city}
                    onChange={(e) => {
                        setZipcode(e.target.value);
                        setUpdated(true);
                    }}
                    helperText={
                        !country
                            ? "Select country first"
                            : !state
                                ? `Select ${labels.region.toLowerCase()} first`
                                : !city
                                    ? "Select city first"
                                    : " "
                    }
                />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    label="Address"
                    variant="outlined"
                    value={address || ""}
                    onChange={(e) => {
                        setAddress(e.target.value);
                        setUpdated(true);
                    }}
                    helperText={labels.street}
                />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={desc}
                    onChange={(e) => {
                        setDesc(e.target.value);
                        setUpdated(true);
                    }}
                    inputProps={{ maxLength: 250 }}
                    helperText={`${desc?.length} / 250`}
                />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={!!smsOptIn}
                            onChange={(e) => {
                                setSmsOptIn(e.target.checked);
                                setSmsConsentChanged(true);
                                setUpdated(true);
                            }}
                        />
                    )}
                    label={
                        <>
                            {SMS_CONSENT_TEXT}
                            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                <Link href="/privacy" target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </Link>
                                {" | "}
                                <Link href="/terms" target="_blank" rel="noopener noreferrer">
                                    Terms & Conditions
                                </Link>
                            </Typography>
                        </>
                    }
                />
            </FormControl>

          <Button
              color="primary"
              variant="contained"
              fullWidth
              onClick={() => handleSubmit()}
                disabled={!updated} // Disable if no changes or while updating
          >
                {"Save Changes"}
          </Button>
            {!!errors && (
                <Alert severity="error" sx={{ mt: 1.25 }}>
                    {errors}
                </Alert>
            )}
            <Snackbar 
              ref={notificationRef}
              autoHideDuration={6000}
              onClose={() => { }}
          >
              <Alert severity="success">Profile updated successfully!</Alert>
          </Snackbar>
      </Typography>
  );
});

export default UserPageGeneral;
